import type { Effect, Reducer } from 'umi';
import { history } from 'umi';
import type { IUserInfo, UserModelState, FeishuLoginResp } from '@/types/user';
import { setAuthority } from '@/utils/authority';
import { login, logout, queryCurrent, feishuLogin, fetchThirdPartyLogin } from '@/services/login';
import { identifyUser, resetPosthogUser } from '@/posthog';

export type CurrentUser = IUserInfo;

export type UserModelType = {
  namespace: 'user';
  state: UserModelState;
  effects: {
    fetchCurrent: Effect;
    // queryCurrent: Effect;
    login: Effect;
    feishuLogin: Effect;
    thirdPartyLogin: Effect;
    logout: Effect;
  };
  reducers: {
    saveCurrentUser: Reducer<UserModelState>;
    setLoading: Reducer<UserModelState>;
    setFeishuLoginError: Reducer<UserModelState>;
    setThirdPartyLoginError: Reducer<UserModelState>;
    setThirdPartyLoginLoading: Reducer<UserModelState>;
    setError: Reducer<UserModelState>;
    clear: Reducer<unknown>;
  };
};

const UserModel: UserModelType = {
  namespace: 'user',
  state: { currentUser: null, feishuLoading: false },
  effects: {
    *fetchCurrent(_, { call, put }) {
      const [currentUser, error] = yield call(queryCurrent);
      yield put({ type: 'saveCurrentUser', payload: { currentUser, error } });

      const userName = currentUser?.name;
      identifyUser(userName, {
        name: userName,
      });
      if (error) {
        history.push('/user/login');
      } else if (window.location.pathname === '/') {
        if (currentUser?.groupName === 'Admin') {
          history.replace('/administration/userGroup');
        } else if (currentUser?.groupName === 'SMB AE') {
          history.replace('/check/closed');
        } else if (['Operation Department', 'Madhouse'].includes(currentUser?.groupName)) {
          history.replace('/research/game');
        } else if (['Game Operation Department'].includes(currentUser?.groupName)) {
          history.replace('/fbProcess/total');
        }
      }
    },
    *login({ payload }, { call, put }) {
      const [currentUser, error] = yield call(login, payload);
      if (!error) {
        yield put({ type: 'saveCurrentUser', payload: { currentUser, error } });
        if (currentUser?.groupName === 'Admin') {
          history.replace('/administration/userGroup');
        } else if (currentUser?.groupName === 'SMB AE') {
          history.replace('/check/closed');
        } else if (['Operation Department', 'Madhouse'].includes(currentUser?.groupName)) {
          history.replace('/research/game');
        } else if (['Game Operation Department'].includes(currentUser?.groupName)) {
          history.replace('/fbProcess/total');
        } else {
          history.replace('/');
        }
      }
    },
    *feishuLogin({ payload }, { call, put }) {
      yield put({ type: 'setLoading', payload: true });
      try {
        // feishuLogin使用的是未包装的umi的request，所以内部的字段都不会转换成小驼峰
        const response: FeishuLoginResp = yield call(feishuLogin, payload);

        if (response?.data?.ka_admin && response?.success) {
          yield put({
            type: 'saveCurrentUser',
            payload: {
              currentUser: {
                ...response?.data?.ka_admin,
                // 这里需要修改为小驼峰
                groupName: response?.data?.ka_admin?.group_name,
              },
            },
          });

          const groupName = response?.data?.ka_admin?.group_name;

          if (groupName === 'Admin') {
            history.replace('/administration/userGroup');
          } else if (groupName === 'SMB AE') {
            history.replace('/check/closed');
          } else if (['Operation Department', 'Madhouse'].includes(groupName)) {
            history.replace('/research/game');
          } else {
            history.replace('/');
          }

          yield put({ type: 'setLoading', payload: false });
          return true;
        } else {
          // 如果不满足条件则设置错误信息
          yield put({ type: 'setFeishuLoginError', payload: '飞书扫码登录失败，请联系管理员' });
          yield put({ type: 'setLoading', payload: false });
          return false;
        }
      } catch {
        // 如果接口请求失败也要设置错误信息
        yield put({ type: 'setFeishuLoginError', payload: '飞书扫码登录失败，请联系管理员' });
        yield put({ type: 'setLoading', payload: false });
        return false;
      }
    },
    *thirdPartyLogin({ payload }, { call, put }) {
      yield put({ type: 'setThirdPartyLoginLoading', payload: true });

      // 接口返回的数据都被变为了小驼峰，为了与之前的数据保持一致，这里需要转换为大驼峰
      const [currentUser, error] = yield call(fetchThirdPartyLogin, payload);

      yield put({ type: 'setThirdPartyLoginLoading', payload: false });

      if (!error) {
        yield put({
          type: 'saveCurrentUser',
          payload: { currentUser: currentUser.kaAdmin, error },
        });
        if (currentUser?.kaAdmin?.groupName === 'Admin') {
          history.replace('/administration/userGroup');
        } else if (currentUser?.kaAdmin?.groupName === 'SMB AE') {
          history.replace('/check/closed');
        } else if (['Operation Department', 'Madhouse'].includes(currentUser?.kaAdmin?.groupName)) {
          history.replace('/research/game');
        } else {
          history.replace('/');
        }
      } else {
        yield put({
          type: 'setThirdPartyLoginError',
          payload: error ?? '第三方登录失败，请联系管理员',
        });
        return error;
      }
    },
    *logout(_, { put, call }) {
      try {
        yield call(logout);
      } catch (e) {
        // yield put({ type: 'saveCurrentUser', payload: { error: error.toString() } });
      } finally {
        yield put({ type: 'clear' });

        resetPosthogUser();

        // const { redirect } = getPageQuery()
        // 重新登录后跳转到当前 URL
        if (window.location.pathname !== '/user/login') {
          // 使用location.replace会刷新页面。不会保留已有的redux的数据。造成部分页面退出重新登录后还留有上一次登录的数据
          history.replace({ pathname: '/user/login' });
          // window.location.replace('/user/login')
        }
      }
    },
  },

  reducers: {
    clear() {
      return { currentUser: null };
    },
    setLoading(state, { payload }) {
      return {
        ...(state as UserModelState),
        feishuLoading: payload,
      };
    },
    setFeishuLoginError(state, { payload }) {
      return {
        ...(state as UserModelState),
        feishuLoginError: payload,
      };
    },
    setThirdPartyLoginError(state, { payload }) {
      return {
        ...(state as UserModelState),
        thirdPartyLoginError: payload,
      };
    },
    setThirdPartyLoginLoading(state, { payload }) {
      return {
        ...(state as UserModelState),
        thirdPartyLoading: payload,
      };
    },
    setError(state, { payload }) {
      return {
        ...(state as UserModelState),
        error: payload,
      };
    },
    saveCurrentUser(state, { payload = {} }) {
      const { groupName, ...r } = payload?.currentUser || {};
      const params = { ...r, groups: [groupName], menus: [groupName] };

      setAuthority(params?.groups || []);
      return {
        ...state,
        currentUser: params,
      };
    },
  },
};

export default UserModel;
